export type BexioReceiptSourceField = 'amount' | 'currency' | 'occurredAt';

const SUPPORTED = new Set<BexioReceiptSourceField>(['amount', 'currency', 'occurredAt']);

/** The backend derives this list from the canonical Bexio Expense contract. */
export function bexioReceiptRequiredFields(value: unknown): BexioReceiptSourceField[] {
  if (!Array.isArray(value)) return [];
  return value.filter((field): field is BexioReceiptSourceField => SUPPORTED.has(field as BexioReceiptSourceField));
}

export function missingBexioReceiptFields(requiredFields: readonly BexioReceiptSourceField[], values: {
  amount: string;
  currency: string;
  occurredAt: string;
}): BexioReceiptSourceField[] {
  const amount = Number(values.amount);
  return requiredFields.filter((field) => {
    if (field === 'amount') return !values.amount.trim() || !Number.isFinite(amount) || amount < 0;
    if (field === 'currency') return !/^[A-Z]{3}$/.test(values.currency.trim().toUpperCase());
    return !/^\d{4}-\d{2}-\d{2}$/.test(values.occurredAt);
  });
}

export function bexioReceiptErrorText(error: unknown, t: (key: string) => string, fallback: string): string {
  const requestError = error as Error & { code?: string; details?: { missingFields?: unknown } };
  if (requestError?.code === 'COSTS_RECEIPT_ACCOUNTING_REQUIRED') {
    const fields = bexioReceiptRequiredFields(requestError.details?.missingFields);
    const labels = fields.map((field) => t(`costs.fields.${field === 'amount' ? 'total' : field === 'occurredAt' ? 'date' : field}`));
    return [t('errors.COSTS_RECEIPT_ACCOUNTING_REQUIRED'), labels.join(', ')].filter(Boolean).join(' ');
  }
  return requestError instanceof Error && requestError.message ? requestError.message : fallback;
}

export function bexioAccountFailureFields(snapshot: unknown): string[] {
  if (!snapshot || typeof snapshot !== 'object') return [];
  const failure = (snapshot as Record<string, unknown>).bexioFailure;
  if (!failure || typeof failure !== 'object') return [];
  const fields = (failure as Record<string, unknown>).accountDataFields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === 'string') : [];
}
